/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: string/number parsing helpers.
// Source: cardiac_report_v0_5_6_dev_2.html lines 5534-5554 (parseLeadingNumber,
// parseSdObservedRatio), v0.8.3.2 lines 5551-5563 (parseLegacyRegularityPct),
// 3328-3344 (extractNumbers, parseNormalRangeSpec),
// 3577-3585 (parseSysDiaDurationObserved), 2774-2792 (parseSysDiaMsFromEvidence).

export function parseLeadingNumber(value) {
  if (value === undefined || value === null) return null
  const match = String(value).match(/-?\d+(?:\.\d+)?/)
  if (!match) return null
  const parsed = Number(match[0])
  return Number.isFinite(parsed) ? parsed : null
}

// Compatibility parser for reports that predate timing-variation wording.
// Current observations begin with ± and express deviation (small = steady),
// not percent regularity (large = steady), so they must never be reused here.
export function parseLegacyRegularityPct(value) {
  if (value === undefined || value === null) return null
  const text = String(value).trim()
  if (!text || text.startsWith("±")) return null
  const match = text.match(/^(\d+(?:\.\d+)?)\s*%(?:\s+regular)?(?:\s|$)/i)
  if (!match) return null
  const parsed = Number(match[1])
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * A cycle-timing range in display units, "1 : 1.6–1.9", as {min, max} of the
 * right-hand side; null for a single value or anything else.
 */
// "1 : 1.6–1.9" with any spacing; matched with the spaces removed
const SD_RATIO_RANGE_PATTERN = /^(-?\d+(?:\.\d+)?)[:/](-?\d+(?:\.\d+)?)[–-](-?\d+(?:\.\d+)?)$/

export function parseSdObservedRatioRange(value) {
  if (value === undefined || value === null) return null
  const match = String(value).replace(/\s+/g, "").match(SD_RATIO_RANGE_PATTERN)
  if (!match) return null
  const left = Number(match[1])
  const low = Number(match[2])
  const high = Number(match[3])
  if (!Number.isFinite(left) || !Number.isFinite(low) || !Number.isFinite(high) || left !== 1) return null
  return { min: Math.min(low, high), max: Math.max(low, high) }
}

// A range ("1 : 1.6–1.9") reads as its midpoint, so the marker and the
// single-value grading have one number to work from; the ends are read by
// parseSdObservedRatioRange.
export function parseSdObservedRatio(value) {
  if (value === undefined || value === null) return null
  const text = String(value).trim()
  const rangeMatch = text.replace(/\s+/g, "").match(SD_RATIO_RANGE_PATTERN)
  if (rangeMatch) {
    const left = Number(rangeMatch[1])
    const midpointRight = (Number(rangeMatch[2]) + Number(rangeMatch[3])) / 2
    if (Number.isFinite(left) && Number.isFinite(midpointRight) && midpointRight !== 0) return left / midpointRight
  }
  const ratioMatch = text.match(/^\s*(-?\d+(?:\.\d+)?)\s*[:/]\s*(-?\d+(?:\.\d+)?)\s*$/)
  if (ratioMatch) {
    const left = Number(ratioMatch[1])
    const right = Number(ratioMatch[2])
    if (Number.isFinite(left) && Number.isFinite(right) && right !== 0) {
      return left / right
    }
  }
  return parseLeadingNumber(text)
}

export function extractNumbers(value) {
  return (String(value).match(/-?\d+(?:\.\d+)?/g) || []).map(Number).filter(Number.isFinite)
}

export function parseNormalRangeSpec(normalRange, featureType) {
  const raw = String(normalRange || "")
  const nums = extractNumbers(raw)
  if (nums.length === 0) return null
  if (raw.includes("<") || raw.includes("≤")) {
    return { kind: "upper", min: 0, max: nums[0] }
  }
  if (raw.includes(">") || raw.includes("≥")) {
    const max = featureType === "percentage" ? 100 : nums[0] * 1.3
    return { kind: "lower", min: nums[0], max }
  }
  return { kind: "range", min: nums[0], max: nums.length > 1 ? nums[1] : nums[0] * 2 }
}

const SYS_DURATION_PATTERN = /\bSYS(?:TOLE)?\s*(\d+(?:\.\d+)?)(?:\s*[–-]\s*(\d+(?:\.\d+)?))?\s*ms/i
const DIA_DURATION_PATTERN = /\bDIA(?:STOLE)?\s*(\d+(?:\.\d+)?)(?:\s*[–-]\s*(\d+(?:\.\d+)?))?\s*ms/i

/**
 * Each phase of a duration entry as its own range: "SYS 280–310 ms · DIA
 * 500–560 ms" gives both ends per phase, a single value gives the same
 * number twice, a missing phase gives nulls.
 */
export function parseSysDiaDurationRanges(observed) {
  const text = String(observed || "")
  const sysMatch = text.match(SYS_DURATION_PATTERN)
  const diaMatch = text.match(DIA_DURATION_PATTERN)
  const ends = (match) => {
    if (!match) return [null, null]
    const low = Number(match[1])
    const high = Number(match[2] || match[1])
    return [Math.min(low, high), Math.max(low, high)]
  }
  const [sysMin, sysMax] = ends(sysMatch)
  const [diaMin, diaMax] = ends(diaMatch)
  return { sysMin, sysMax, diaMin, diaMax }
}

// A phase given as a range ("SYS 280–310 ms") reads as its midpoint; the
// ends are read by parseSysDiaDurationRanges.
export function parseSysDiaDurationObserved(observed) {
  const text = String(observed || "")
  const sysMatch = text.match(SYS_DURATION_PATTERN)
  const diaMatch = text.match(DIA_DURATION_PATTERN)
  // A bare "608 ms" is a legacy single-value entry; it was always the
  // diastolic duration.
  const bareDurationMatch = !sysMatch && !diaMatch ? text.match(/^\s*(\d+(?:\.\d+)?)\s*ms\s*$/i) : null
  const midpoint = (match) => (match ? (Number(match[1]) + Number(match[2] || match[1])) / 2 : null)
  return {
    sysMs: midpoint(sysMatch),
    diaMs: diaMatch ? midpoint(diaMatch) : bareDurationMatch ? Number(bareDurationMatch[1]) : null,
  }
}

// NOTE: falls back to regexing display/finding TEXT for numeric values the
// pipeline already owns as numbers (sysMs/diaMs are present in our export's
// cardiacCycleTiming evidence) — which module should own these values is an
// open design question. Retained verbatim for parity.
export function parseSysDiaMsFromEvidence(incoming) {
  if (!incoming) return {}
  const sys = Number(incoming.sysMs ?? incoming.systoleMs ?? incoming.meanSysMs)
  const dia = Number(incoming.diaMs ?? incoming.diastoleMs ?? incoming.meanDiaMs)
  if (Number.isFinite(sys) || Number.isFinite(dia)) {
    return {
      sysMs: Number.isFinite(sys) ? sys : null,
      diaMs: Number.isFinite(dia) ? dia : null,
    }
  }
  const text = String(incoming.finding || incoming.observed || "")
  const match =
    /(?:mean|median) systole\s+(\d+(?:\.\d+)?)\s*ms\s+and\s+diastole\s+(\d+(?:\.\d+)?)\s*ms/i.exec(text) ||
    /sys(?:tole)?\s*(\d+(?:\.\d+)?)\s*ms.*dia(?:stole)?\s*(\d+(?:\.\d+)?)\s*ms/i.exec(text)
  if (!match) return {}
  return {
    sysMs: Number(match[1]),
    diaMs: Number(match[2]),
  }
}
